import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';
import { notifyAppError } from '@/services/utils/notifyAppError';
import { setAccessToken } from '@/features/authentication/authenticationSlice';

const BASE_URL = process.env.NEXT_PUBLIC_REPAROLY_API_URL ?? 'https://api.reparoly.local';

export interface ApiResponseBase<T> {
  success: boolean;
  message: string;
  data: T;
}

export type SignupInput = { email: string; password: string; aud?: string };
export type SignupResult = { token?: string | null; brand_id?: number | null; user?: any };
export type LoginInput = { email?: string; username?: string; password: string; aud?: string };
export type LoginResult = {
  token: string;
  brand_id: number;
  user: {
    id: number;
    username: string;
    display_name: string;
    role_id: number;
    company_id: number;
  };
};
export type RefreshResult = { token?: string | null };
export type LogoutResult = { success?: boolean };

export type UserProfile = {
  id: number;
  email: string;
  username: string;
  company_id: number;
  display_name: string;
  role_id: number;
  status_id: number;
  phone_number: string | null;
  avatar_url?: string | null;
  permission_codes: string[];
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  endpoints: (builder) => ({
    // POST /v1/auth/login
    login: builder.mutation<LoginResult, LoginInput>({
      query: (body) => ({ url: '/v1/auth/login', method: 'POST', body }),
      transformResponse: (resp: ApiResponseBase<LoginResult>) =>
        resp?.data ?? {
          token: '',
          brand_id: 0,
          user: {
            id: 0,
            username: '',
            display_name: '',
            role_id: 0,
            company_id: 0,
          },
        },
      transformErrorResponse: handlerErrorResponse('Auth.Login'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'Auth.Login',
            fallbackMessage: 'Error al iniciar sesión',
            action: 'auth.login',
          });
        }
      },
    }),
    // POST /v1/auth/refresh
    refresh: builder.mutation<ApiResponseBase<RefreshResult>, void | { refresh_token?: string }>({
      query: (body) => ({ url: '/v1/auth/refresh', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Auth.Refresh'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const token = (data as any)?.data?.token || null;
          // Guarda el access token para que posteriores queries (p.ej. me) adjunten Authorization
          dispatch(setAccessToken({ accessToken: token }));
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'Auth.Refresh',
            fallbackMessage: 'Error al renovar la sesión',
            action: 'auth.refresh',
          });
        }
      },
    }),
    // GET /v1/profile
    profile: builder.query<UserProfile, void>({
      query: () => ({ url: '/v1/users/profile', method: 'GET' }),
      // Mantener datos mucho tiempo para evitar refetchs al cambiar de área (/app <-> /dashboard)
      keepUnusedDataFor: 24 * 60 * 60, // 24h
      transformResponse: (resp: ApiResponseBase<UserProfile>): UserProfile => resp?.data,
      transformErrorResponse: handlerErrorResponse('Auth.Me'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'Auth.Me',
            fallbackMessage: 'Error al obtener tu información',
            action: 'auth.me',
          });
        }
      },
    }),
    // POST /v1/auth/logout
    logout: builder.mutation<ApiResponseBase<LogoutResult> | { success?: boolean }, void>({
      query: () => ({ url: '/v1/auth/logout', method: 'POST' }),
      transformErrorResponse: handlerErrorResponse('Auth.Logout'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'Auth.Logout',
            fallbackMessage: 'Error al cerrar sesión',
            action: 'auth.logout',
          });
        }
      },
    }),
    // POST /v1/auth/signup
    signup: builder.mutation<ApiResponseBase<SignupResult>, SignupInput>({
      query: (body) => ({ url: '/v1/auth/signup', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Auth.Signup'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          notifyAppError(dispatch, err, {
            endpointName: 'Auth.Signup',
            fallbackMessage: 'Error al crear la cuenta',
            action: 'auth.signup',
          });
        }
      },
    }),
  }),
});

export const {
  useSignupMutation,
  useLoginMutation,
  useRefreshMutation,
  useProfileQuery,
  useLazyProfileQuery,
  useLogoutMutation,
} = authApi;
